import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import { useUserContext } from '@fff-web/fff-utilities';
import { SubscriptionUpdatePaymentModal, useCouponCode } from '@fff-web/fff-payments';
import { trackEvent, trackPageLoad } from '@fff-web/fff-analytics';
import { PSClickWrap } from '@pactsafe/pactsafe-react-sdk';
import { redirectToBaseSite } from '../../utils/redirectMethods';
import useCouponContent from '../../hooks/useCouponContent';
import { useQueryParams } from '../../utils/hooks';
import useShowErrorOrRedirectThankYouPage from '../../hooks/useShowPayErrorOrThankYouPage';
import {
  UpgradeMembershipProvider,
  useUpgradeMembershipContext,
} from '../../utils/ContextProviders/UpgradeContext';
import { couponScreenKey, Plans } from '../constants';
import env from '../MyAccount/utils/env';
import Layout from '../../components/Layout';
import AllErrorMessage from '../../components/AllErrorMessage';
import { GROWTH_UPGRADE_MEMBERSHIP } from '../../utils/Optimizely/featureFlagVariations';
import { UpgradeRedesign } from './UpgradeRedesign';
import UpgradeControl from '../Upgrade/Upgrade';
import useLaunchDarkly from '../../hooks/useLaunchDarkly';

// This component hold the upgrade page logic
const UpgradeContainer = ({ ldClient }) => {
  const { userInfo } = useUserContext();
  const [disableClickWrapSending, setDisableClickWrapSending] = useState(false);
  const { growthUpgrade } = useUpgradeMembershipContext();
  const search = useQueryParams();
  const {
    coupon,
    price,
    handleReturnPrice,
    handleOnChange,
    // @ts-expect-error - TODO: Please review useCouponCode since handleSubmit is not returned by it.
    handleSubmit,
    resetInput,
  } = useCouponCode({ initialCouponCode: search.coupon_code });

  const upgradeCaptchaRef = useRef(null);
  const history = useHistory();
  const thanksPage = '/upgrade-thanks';

  const { getCouponContentKey } = useCouponContent(couponScreenKey.UPGRADE);

  // TODO: Replace the submit logic to use only the coupon code string and not the object, now the upgrade page and reactivate page are using the same submit logic
  const discount = price?.now?.discount;
  const applyValidCoupon = parseFloat(discount) > 0;

  const couponsApplied = applyValidCoupon ? [{ code: coupon, type: 'coupon_code' }] : [];

  const { showRetry, showPayment, errorPaymentMessage, changeSubscription, onClose, isLoading } =
    useShowErrorOrRedirectThankYouPage(history, thanksPage, Plans.SEASONAL, couponsApplied);

  useLaunchDarkly(ldClient, userInfo);

  const isSeasonal =
    userInfo?.user?.subscriptionType === 'SEASONAL' &&
    userInfo?.user?.subscriptionState === 'ACTIVE';

  const hCaptcha = (
    <HCaptcha
      sitekey={env.hCaptchaSiteKey}
      onVerify={() => undefined}
      size="invisible"
      ref={upgradeCaptchaRef}
    />
  );

  useEffect(() => {
    if (isSeasonal) {
      trackPageLoad({
        pathName: '/upgrade-confirmation',
        properties: {
          title: 'Upgrade Page',
          coupon_code: search.coupon_code,
        },
      });
    }
  }, []);

  if (userInfo?.loggedIn && !isSeasonal) {
    redirectToBaseSite();
    return null;
  }

  // This component is used to render the upgrade page with the redesign or control variation
  const RenderUpgradeVariation =
    growthUpgrade.variationKey === GROWTH_UPGRADE_MEMBERSHIP.VARIATION_C
      ? UpgradeRedesign
      : UpgradeControl;

  const clickWrapConfiguration = {
    signerId: userInfo?.user.email,
    accessId: env.pactSafeAccessId,
    testMode: env.appEnv === 'dev',
    groupKey: env.pactSafeUpgradeGroupKey,
    groupId: env.pactSafeUpgradeGroupId,
    snapshotLocation: env.pactSafeUpgradeSnapshotId,
  };

  const isCheckboxChecked = useCallback(() => {
    const checkboxElement = document.getElementById(
      `ps-contract-checkbox-group-${clickWrapConfiguration?.groupId}`,
    ) as HTMLInputElement;
    if (clickWrapConfiguration?.signerId && checkboxElement && !checkboxElement.checked) {
      checkboxElement.click();
      checkboxElement.disabled = true;
      setDisableClickWrapSending(true);
    }
    return checkboxElement;
  }, [clickWrapConfiguration?.groupId, clickWrapConfiguration?.signerId]);

  const memoizedIsCheckboxChecked = useMemo(() => isCheckboxChecked, [isCheckboxChecked]);

  const onSubmitSubscription = () => {
    upgradeCaptchaRef.current.resetCaptcha();
    trackEvent({
      ctaName: 'upgrade-confirmation',
      properties: {
        upgrading_plan: Plans.ANNUAL,
        CTA: 'upgrade-confirmation',
      },
    });
    upgradeCaptchaRef.current
      .execute({ async: true })
      .then(({ response: captchaToken }) => {
        memoizedIsCheckboxChecked();
        changeSubscription(Plans.ANNUAL, captchaToken);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Error processing you request:', err);
      });
  };

  return (
    <UpgradeMembershipProvider>
      <Layout
        isLoading={isLoading}
        showError={showRetry}
        redirectIfNoAuth
        ErrorComponent={() => <AllErrorMessage subjectAction="upgrade" onButtonClick={onClose} />}
      >
        <RenderUpgradeVariation
          couponProps={{
            coupon,
            price,
            initialCouponCode: search.coupon_code,
            plan: Plans.ANNUAL,
            handleReturnPrice,
            handleOnChange,
            handleSubmit,
            resetInput,
          }}
          hCaptcha={hCaptcha}
          getCouponContentKey={getCouponContentKey}
          onSubmitSubscription={onSubmitSubscription}
          isLoading={isLoading}
        />
        <SubscriptionUpdatePaymentModal
          onClose={onClose}
          subtitle={errorPaymentMessage}
          isOpen={showPayment}
          onSuccess={onSubmitSubscription}
          hasError={!!errorPaymentMessage}
        />
        <div className="pactSafeWrapper">
          <PSClickWrap
            signerId={clickWrapConfiguration.signerId}
            accessId={clickWrapConfiguration.accessId}
            groupKey={clickWrapConfiguration.groupKey}
            snapshotLocation={clickWrapConfiguration.snapshotLocation}
            disableSending={disableClickWrapSending}
            displayImmediately={false}
            testMode={clickWrapConfiguration.testMode}
            clickWrapStyle="combined"
          />
        </div>
      </Layout>
    </UpgradeMembershipProvider>
  );
};

export { UpgradeContainer };
